import {
  getAuthSessionState,
  type StoredActiveOrganization,
  type StoredAuthSession,
  updateAuthSessionActiveOrganization,
} from "../config/auth-session";
import { resolveRuntimeContext } from "../runtime/context";
import { createPresenter } from "../runtime/presenter/index";
import type { Presenter } from "../runtime/presenter/types";
import type { RuntimeContext } from "../runtime/types";
import { renderInfoCard } from "../ui/info-card";
import {
  AuthOrganizationApiError,
  listAuthOrganizations,
  setActiveAuthOrganization,
} from "./auth-organization-api";

type OrganizationCommandContext = {
  runtime: RuntimeContext;
  presenter: Presenter;
  session: StoredAuthSession;
};

type OrganizationCommandErrorInput = {
  presenter: Presenter;
  code: string;
  title: string;
  message: string;
  nextStep: string;
  activeOrganization?: StoredActiveOrganization | null;
  details?: Array<{ label: string; value: string }>;
  payload?: Record<string, unknown>;
};

export async function runOrgList(input?: { view?: string }): Promise<void> {
  const context = await resolveOrganizationCommandContext(input);
  if (!context) {
    return;
  }

  const organizations = await loadOrganizationsOrEmitError(context);
  if (!organizations) {
    return;
  }

  const activeOrganization = resolveActiveOrganization({
    activeOrganization: context.session.activeOrganization ?? null,
    organizations,
  });
  if (
    context.session.activeOrganization &&
    organizations.length > 0 &&
    !activeOrganization
  ) {
    emitOrganizationCommandError({
      presenter: context.presenter,
      code: "organization.active_not_member",
      title: "Organization Mismatch",
      message:
        "The cached active organization is not in your WakeLoop organization list.",
      nextStep:
        "Run `wakeloop org switch <slug>` to choose a valid organization.",
      activeOrganization: context.session.activeOrganization,
      payload: {
        organizations: buildOrganizationListPayload({
          organizations,
          activeOrganization: null,
        }).organizations,
      },
    });
    return;
  }

  const payload = buildOrganizationListPayload({
    organizations,
    activeOrganization,
  });
  if (context.runtime.resolvedView === "human") {
    renderOrganizationListCard({
      presenter: context.presenter,
      organizations,
      activeOrganization,
    });
    return;
  }

  context.presenter.data({
    code: "organization.list",
    payload,
  });
}

export async function runOrgCurrent(input?: { view?: string }): Promise<void> {
  const context = await resolveOrganizationCommandContext(input);
  if (!context) {
    return;
  }

  const activeOrganization = context.session.activeOrganization ?? null;
  if (!activeOrganization) {
    emitOrganizationCommandError({
      presenter: context.presenter,
      code: "organization.active_missing",
      title: "No Active Organization",
      message: "No active WakeLoop organization is selected.",
      nextStep: "Run `wakeloop org list`, then `wakeloop org switch <slug>`.",
      activeOrganization: null,
    });
    return;
  }

  const payload = buildCurrentOrganizationPayload(activeOrganization);
  if (context.runtime.resolvedView === "human") {
    renderCurrentOrganizationCard({
      presenter: context.presenter,
      title: "Current Organization",
      organization: activeOrganization,
    });
    return;
  }

  context.presenter.data({
    code: "organization.current",
    payload,
  });
}

export async function runOrgSwitch(input: {
  slug?: string;
  view?: string;
}): Promise<void> {
  const targetSlug = normalizeOrganizationSlug(input.slug);
  const runtime = await resolveRuntimeContext({ view: input.view });
  const presenter = createPresenter(runtime);
  if (!targetSlug) {
    emitOrganizationCommandError({
      presenter,
      code: "organization.slug_required",
      title: "Organization Slug Required",
      message: "Choose the organization slug to make active.",
      nextStep: "Run `wakeloop org switch <slug>`.",
      activeOrganization: null,
    });
    return;
  }

  const context = await resolveOrganizationCommandContext(input, {
    runtime,
    presenter,
  });
  if (!context) {
    return;
  }

  const organizations = await loadOrganizationsOrEmitError(context);
  if (!organizations) {
    return;
  }

  const target = organizations.find(
    (organization) => organization.slug === targetSlug
  );
  if (!target) {
    emitOrganizationCommandError({
      presenter: context.presenter,
      code: "organization.not_found",
      title: "Organization Not Found",
      message: `You are not a member of a WakeLoop organization with slug "${targetSlug}".`,
      nextStep:
        "Run `wakeloop org list` to see organizations available to this account.",
      activeOrganization: context.session.activeOrganization ?? null,
      payload: {
        requestedSlug: targetSlug,
        organizations: buildOrganizationListPayload({
          organizations,
          activeOrganization: context.session.activeOrganization ?? null,
        }).organizations,
      },
    });
    return;
  }

  try {
    await setActiveAuthOrganization({
      session: context.session,
      organizationSlug: target.slug,
    });
    await updateAuthSessionActiveOrganization(target);
  } catch (error) {
    emitOrganizationApiError({
      presenter: context.presenter,
      error,
      activeOrganization: context.session.activeOrganization ?? null,
    });
    return;
  }

  const payload = {
    status: "ok",
    organization: {
      active: target,
    },
  };
  if (context.runtime.resolvedView === "human") {
    renderCurrentOrganizationCard({
      presenter: context.presenter,
      title: "Active Organization Updated",
      organization: target,
    });
    return;
  }

  context.presenter.data({
    code: "organization.switch",
    payload,
  });
}

async function resolveOrganizationCommandContext(
  input?: { view?: string },
  resolved?: {
    runtime: RuntimeContext;
    presenter: Presenter;
  }
): Promise<OrganizationCommandContext | null> {
  const runtime =
    resolved?.runtime ?? (await resolveRuntimeContext({ view: input?.view }));
  const presenter = resolved?.presenter ?? createPresenter(runtime);
  const authState = await getAuthSessionState({
    validateRemote: true,
  });
  if (authState.state !== "valid" || !authState.session) {
    emitOrganizationCommandError({
      presenter,
      code: "organization.auth_required",
      title: "Sign In Needed",
      message: "Sign in to WakeLoop before managing organizations.",
      nextStep: "Run `wakeloop login`, then retry this command.",
      activeOrganization: null,
      payload: {
        authState: authState.state,
        reasonCode: authState.reasonCode ?? null,
      },
    });
    return null;
  }

  return {
    runtime,
    presenter,
    session: authState.session,
  };
}

async function loadOrganizationsOrEmitError(
  context: OrganizationCommandContext
): Promise<StoredActiveOrganization[] | null> {
  try {
    return await listAuthOrganizations(context.session);
  } catch (error) {
    emitOrganizationApiError({
      presenter: context.presenter,
      error,
      activeOrganization: context.session.activeOrganization ?? null,
    });
    return null;
  }
}

function emitOrganizationApiError(input: {
  presenter: Presenter;
  error: unknown;
  activeOrganization: StoredActiveOrganization | null;
}): void {
  if (input.error instanceof AuthOrganizationApiError) {
    emitOrganizationCommandError({
      presenter: input.presenter,
      code: input.error.code,
      title:
        input.error.code === "organization.auth_revoked"
          ? "Sign In Needed"
          : "Organization Error",
      message: input.error.message,
      nextStep:
        input.error.code === "organization.auth_revoked"
          ? "Run `wakeloop login`, then retry this command."
          : "Retry the command after WakeLoop auth is reachable.",
      activeOrganization: input.activeOrganization,
      details: [
        ...(input.error.status === null
          ? []
          : [{ label: "HTTP Status", value: String(input.error.status) }]),
        ...(input.error.requestId
          ? [{ label: "Request ID", value: input.error.requestId }]
          : []),
        ...(input.error.detailMessage
          ? [{ label: "Details", value: input.error.detailMessage }]
          : []),
      ],
    });
    return;
  }

  emitOrganizationCommandError({
    presenter: input.presenter,
    code: "organization.unexpected_error",
    title: "Organization Error",
    message:
      input.error instanceof Error
        ? input.error.message
        : "Could not complete the organization command.",
    nextStep: "Retry the command after WakeLoop auth is reachable.",
    activeOrganization: input.activeOrganization,
  });
}

function emitOrganizationCommandError(
  input: OrganizationCommandErrorInput
): void {
  process.exitCode = 1;
  const payload = {
    status: "error",
    organization: {
      active: input.activeOrganization ?? null,
    },
    error: {
      code: input.code,
      message: input.message,
      nextStep: input.nextStep,
    },
    ...(input.payload ?? {}),
  };

  if (input.presenter.view === "human") {
    renderInfoCard({
      presenter: input.presenter,
      title: input.title,
      codePrefix: "organization.error.card",
      rows: [
        { label: "Reason", value: input.message },
        { label: "Next Step", value: input.nextStep },
        ...(input.details ?? []),
      ],
      sanitize: true,
      minContentWidth: 72,
    });
    return;
  }

  input.presenter.data({
    code: input.code,
    payload,
  });
}

function renderOrganizationListCard(input: {
  presenter: Presenter;
  organizations: StoredActiveOrganization[];
  activeOrganization: StoredActiveOrganization | null;
}): void {
  renderInfoCard({
    presenter: input.presenter,
    title: "Organizations",
    codePrefix: "organization.list.card",
    rows: [
      {
        label: "Active Organization",
        value: input.activeOrganization
          ? formatOrganizationLabel(input.activeOrganization)
          : "not selected",
      },
      ...input.organizations.map((organization) => ({
        label: "Organization",
        value: `${formatOrganizationLabel(organization)}${
          isSameOrganization(organization, input.activeOrganization)
            ? " [active]"
            : ""
        }`,
      })),
    ],
    sanitize: true,
    minContentWidth: 72,
  });
}

function renderCurrentOrganizationCard(input: {
  presenter: Presenter;
  title: string;
  organization: StoredActiveOrganization;
}): void {
  renderInfoCard({
    presenter: input.presenter,
    title: input.title,
    codePrefix: "organization.current.card",
    rows: [
      { label: "Name", value: input.organization.name },
      { label: "Slug", value: input.organization.slug },
      { label: "Organization ID", value: input.organization.id },
      ...(input.organization.role
        ? [{ label: "Role", value: input.organization.role }]
        : []),
    ],
    sanitize: true,
    minContentWidth: 72,
  });
}

function buildOrganizationListPayload(input: {
  organizations: StoredActiveOrganization[];
  activeOrganization: StoredActiveOrganization | null;
}) {
  return {
    status: "ok",
    organization: {
      active: input.activeOrganization,
    },
    organizations: input.organizations.map((organization) => ({
      ...organization,
      active: isSameOrganization(organization, input.activeOrganization),
    })),
  };
}

function buildCurrentOrganizationPayload(
  activeOrganization: StoredActiveOrganization
) {
  return {
    status: "ok",
    organization: {
      active: activeOrganization,
    },
  };
}

function resolveActiveOrganization(input: {
  activeOrganization: StoredActiveOrganization | null;
  organizations: StoredActiveOrganization[];
}): StoredActiveOrganization | null {
  if (!input.activeOrganization) {
    return null;
  }
  return (
    input.organizations.find((organization) =>
      isSameOrganization(organization, input.activeOrganization)
    ) ?? null
  );
}

function isSameOrganization(
  left: StoredActiveOrganization | null | undefined,
  right: StoredActiveOrganization | null | undefined
): boolean {
  if (!(left && right)) {
    return false;
  }
  return left.id === right.id || left.slug === right.slug;
}

function formatOrganizationLabel(
  organization: StoredActiveOrganization
): string {
  const role = organization.role ? `, ${organization.role}` : "";
  return `${organization.name} (${organization.slug})${role}`;
}

function normalizeOrganizationSlug(value: unknown): string | null {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized.length > 0 ? normalized : null;
}
